import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

export interface EntraUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  department?: string;
  jobTitle?: string;
  accountEnabled: boolean;
  userType: string;
  groups: any[];
  roles: any[];
  mfaEnabled: boolean;
  mfaMethods: string[];
  isRisky: boolean;
  entraId: string;
  lastSyncedAt: string;
}

interface EntraIntegrationResult {
  users: EntraUser[];
  summary: {
    totalUsers: number;
    newUsers: number;
    updatedUsers: number;
    skippedUsers: number;
    errors: number;
    syncedAt: string;
  };
}

interface CachedEntraData {
  data: EntraIntegrationResult;
  cachedAt: number;
  expiresAt: number;
}

const CACHE_KEY = 'entraUsersCache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export class EntraUserService {
  private static instance: EntraUserService;
  private cache: Map<string, CachedEntraData> = new Map();

  static getInstance(): EntraUserService {
    if (!EntraUserService.instance) {
      EntraUserService.instance = new EntraUserService();
    }
    return EntraUserService.instance;
  }

  /**
   * Get Entra users from cache or fetch from integration
   */
  async getEntraUsers(tenantId: string, forceRefresh = false): Promise<EntraIntegrationResult | null> {
    const cacheKey = `${CACHE_KEY}_${tenantId}`;
    
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        console.log('[EntraUserService] Using cached Entra users data');
        return cached.data;
      }
    }

    try {
      console.log('[EntraUserService] Fetching Entra users from integration');
      
      // Fetch the latest integration execution result
      const response = await deiterateAPI.GET(
        `/v1/integrations/events?status=completed&integrationId=entra-user-sync&limit=1`,
        {
          backend: 'thalassa',
          headers: {
            'x-tenancy-id': tenantId,
            'company-id': tenantId,
            'customer-id': tenantId,
          },
        }
      );

      if (response && response.events && response.events.length > 0) {
        const latestEvent = response.events[0];
        
        if (latestEvent.status === 'completed' && latestEvent.result) {
          const result: EntraIntegrationResult = latestEvent.result;
          
          // Cache the result
          this.setCache(cacheKey, result);
          
          return result;
        }
      }

      console.log('[EntraUserService] No successful execution found');
      return null;
    } catch (error) {
      console.error('[EntraUserService] Error fetching Entra users:', error);
      return null;
    }
  }

  /**
   * Trigger the Entra user sync integration
   */
  async triggerEntraSync(tenantId: string): Promise<boolean> {
    try {
      console.log('[EntraUserService] Triggering Entra user sync');
      
      const response = await deiterateAPI.POST(
        `/v1/integrations/entra-user-sync/trigger`,
        {
          backend: 'thalassa',
          headers: {
            'x-tenancy-id': tenantId,
            'company-id': tenantId,
            'customer-id': tenantId,
          },
          body: {
            payload: {},
          },
        }
      );

      return response?.statusCode === 200;
    } catch (error) {
      console.error('[EntraUserService] Error triggering Entra sync:', error);
      return false;
    }
  }

  /**
   * Match Entra users with existing de.iterate users by email
   */
  matchEntraUsersWithExisting(entraUsers: EntraUser[], existingUsers: any[]): Array<EntraUser & { isExisting: boolean; existingUserId?: string }> {
    const existingEmails = new Set(existingUsers.map(u => u.email.toLowerCase()));
    
    return entraUsers.map(entraUser => ({
      ...entraUser,
      isExisting: existingEmails.has(entraUser.email.toLowerCase()),
      existingUserId: existingUsers.find(u => u.email.toLowerCase() === entraUser.email.toLowerCase())?.id,
    }));
  }

  /**
   * Get cached data if it's still valid
   */
  private getFromCache(key: string): CachedEntraData | null {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() > cached.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    
    return cached;
  }

  /**
   * Set data in cache
   */
  private setCache(key: string, data: EntraIntegrationResult): void {
    const now = Date.now();
    this.cache.set(key, {
      data,
      cachedAt: now,
      expiresAt: now + CACHE_DURATION,
    });
  }

  /**
   * Clear cache for a specific tenant
   */
  clearCache(tenantId: string): void {
    const cacheKey = `${CACHE_KEY}_${tenantId}`;
    this.cache.delete(cacheKey);
  }

  /**
   * Check Entra client secret expiry
   */
  async checkSecretExpiry(tenantId: string): Promise<{
    secretExpiryDate: string | null;
    daysUntilExpiry: number | null;
    isExpiringSoon: boolean;
    isExpired: boolean;
    allCredentials?: Array<{
      keyId: string;
      displayName?: string;
      startDateTime: string;
      endDateTime: string;
      daysUntilExpiry: number;
    }>;
  } | null> {
    try {
      console.log('[EntraUserService] Checking secret expiry');
      
      const response = await deiterateAPI.POST(
        `/v1/integrations/entra-user-sync/trigger`,
        {
          backend: 'thalassa',
          headers: {
            'x-tenancy-id': tenantId,
            'company-id': tenantId,
            'customer-id': tenantId,
          },
          body: {
            payload: {
              action: 'checkExpiry',
            },
          },
        }
      );

      if (response?.statusCode === 200 && response?.result) {
        return response.result;
      }

      return null;
    } catch (error) {
      console.error('[EntraUserService] Error checking secret expiry:', error);
      return null;
    }
  }
}

export const entraUserService = EntraUserService.getInstance();
